/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import selectSportsbookState from '../../sportsbook-state-selector';
import { RootState, ThunkAction } from '../../../../../store/types';
import { fetchSelections as apiFetchSelections } from '../../api/sportsbook-api';
import reportSportsbookError from '../../utils/sportsbook-error-reporter';
import { addSelections } from '../betslip/ducks';
import { OddsFormat } from '../../sportsbook-types';
import {
	generateBetBuilderUrl as apiGenerateBetBuilderUrl,
	getBetBuilderItem as apiGetBetBuilderItem
} from '../../api/betting-service-api';
import { SelectionDto } from '../../api/sbapi/sbapi-api-model';
import {
	selectSportsbookBetBuilderLocaleFallbackEnabled,
	selectSportsbookBetBuilderTimeout,
	selectSportsbookUnifiedFeedEnabled
} from '../../../../brand/ducks/selectors';
import SportsbookSelectionTrackingTags from '~/components/sportsbook/lobby/sportsbook-selection-tracking-tags';
import normalizeUnifiedId from '../../utils/normalize-unified-id';

type State = {
	isOpen: boolean;
	betBuilderId: string | null;
	isGeneratingBetBuilderUrl: boolean;
	betBuilderUrl: string | null;
	isAddingToBetslip: boolean;
	addStartedOn: number | null;
};

// Selectors
const selectState = (state: RootState) => selectSportsbookState(state).betBuilderModal;
export const selectIsOpen = (state: RootState) => selectState(state).isOpen;
export const selectBetBuilderId = (state: RootState) => selectState(state).betBuilderId;
export const selectBetBuilderUrl = (state: RootState) => selectState(state).betBuilderUrl;
export const selectIsGeneratingBetBuilderUrl = (state: RootState) => selectState(state).isGeneratingBetBuilderUrl;
export const selectIsAddingToBetslip = (state: RootState) => selectState(state).isAddingToBetslip;
export const selectAddStartedOn = (state: RootState) => selectState(state).addStartedOn;

// Reducers
const initialState: State = {
	isOpen: false,
	betBuilderId: null,
	isGeneratingBetBuilderUrl: false,
	betBuilderUrl: null,
	isAddingToBetslip: false,
	addStartedOn: null
};

const handleOpened = (state: State) => {
	state.isOpen = true;
};

const handleClosed = (state: State) => {
	state.isOpen = false;
	state.betBuilderId = null;
	state.isGeneratingBetBuilderUrl = false;
	state.betBuilderUrl = null;
	state.isAddingToBetslip = false;
	state.addStartedOn = null;
};

const handleGenerateBetBuilderUrlStarted = (state: State, action: PayloadAction<string | null>) => {
	const betBuilderId = action.payload;

	if (state.betBuilderId === betBuilderId) {
		return;
	}

	state.betBuilderId = betBuilderId;
	state.isGeneratingBetBuilderUrl = true;
	state.betBuilderUrl = null;
};

const handleGenerateBetBuilderUrlCompleted = (
	state: State,
	action: PayloadAction<{ betBuilderId: string | null; url: string }>
) => {
	const { betBuilderId, url } = action.payload;

	if (state.betBuilderId !== betBuilderId) {
		return;
	}

	state.isGeneratingBetBuilderUrl = false;
	state.betBuilderUrl = url;
};

const handleGenerateBetBuilderUrlFailed = (
	state: State,
	action: PayloadAction<{ betBuilderId: string | null; error: unknown }>
) => {
	const { betBuilderId } = action.payload;

	if (state.betBuilderId !== betBuilderId) {
		return;
	}

	state.isGeneratingBetBuilderUrl = false;
};

const handleAddToBetslipStarted = (
	state: State,
	action: PayloadAction<{ betBuilderId: string | null; addStartedOn: number }>
) => {
	const { betBuilderId, addStartedOn } = action.payload;

	if (state.betBuilderId !== betBuilderId) {
		return;
	}

	state.isAddingToBetslip = true;
	state.addStartedOn = addStartedOn;
};

const handleAddToBetslipCompleted = (state: State, action: PayloadAction<{ betBuilderId: string | null }>) => {
	const { betBuilderId } = action.payload;

	if (state.betBuilderId !== betBuilderId) {
		return;
	}

	state.isAddingToBetslip = false;
	state.addStartedOn = null;
};

const handleAddToBetslipFailed = (
	state: State,
	action: PayloadAction<{ betBuilderId: string | null; error: unknown }>
) => {
	const { betBuilderId } = action.payload;

	if (state.betBuilderId !== betBuilderId) {
		return;
	}

	state.isAddingToBetslip = false;
	state.addStartedOn = null;
};

const betBuilderModalSlice = createSlice({
	name: 'betBuilderModal',
	initialState,
	reducers: {
		opened: handleOpened,
		closed: handleClosed,
		generateBetBuilderUrlStarted: handleGenerateBetBuilderUrlStarted,
		generateBetBuilderUrlCompleted: handleGenerateBetBuilderUrlCompleted,
		generateBetBuilderUrlFailed: handleGenerateBetBuilderUrlFailed,
		addToBetslipStarted: handleAddToBetslipStarted,
		addToBetslipCompleted: handleAddToBetslipCompleted,
		addToBetslipFailed: handleAddToBetslipFailed
	}
});

const {
	opened,
	closed,
	generateBetBuilderUrlStarted,
	generateBetBuilderUrlCompleted,
	generateBetBuilderUrlFailed,
	addToBetslipStarted,
	addToBetslipCompleted,
	addToBetslipFailed
} = betBuilderModalSlice.actions;

const generateBetBuilderUrl =
	(betBuilderId: string, oddsFormat: OddsFormat, hasReversedOrder: boolean): ThunkAction =>
	async dispatch => {
		dispatch(generateBetBuilderUrlStarted(betBuilderId));
		try {
			const url = await apiGenerateBetBuilderUrl(betBuilderId, oddsFormat, hasReversedOrder);
			dispatch(generateBetBuilderUrlCompleted({ betBuilderId, url }));
		} catch (error) {
			dispatch(generateBetBuilderUrlFailed({ betBuilderId, error }));
		}
	};

export const openBetBuilder =
	(betBuilderId: string, oddsFormat: OddsFormat, hasReversedOrder: boolean): ThunkAction =>
	dispatch => {
		dispatch(opened());
		dispatch(generateBetBuilderUrl(betBuilderId, oddsFormat, hasReversedOrder));
	};

export const closeBetBuilder = (): ThunkAction => (dispatch, getState) => {
	if (selectIsAddingToBetslip(getState())) {
		const betBuilderBetId = selectBetBuilderId(getState()) || '';
		const addStartedOn = selectAddStartedOn(getState());
		const waitTimeInMs = addStartedOn ? `${new Date().getTime() - addStartedOn}` : '';
		const configuredTimeoutInSeconds = selectSportsbookBetBuilderTimeout(getState()).toString(); // Seconds
		const isLocaleFallbackEnabled = selectSportsbookBetBuilderLocaleFallbackEnabled(getState()).toString();

		reportSportsbookError('Player pre-emptively closed the bet builder modal', null, {
			betBuilderBetId,
			waitTimeInMs,
			configuredTimeoutInSeconds,
			isLocaleFallbackEnabled
		});
	}

	dispatch(closed());
};

const getSelectionFromSbapi = async (selectionId: string, isLocaleFallbackEnabled: boolean) => {
	const selections = await apiFetchSelections([selectionId], isLocaleFallbackEnabled);
	if (selections.length === 0) {
		return null;
	}
	return selections[0];
};

const sleep = (ms: number) =>
	new Promise(resolve => {
		setTimeout(resolve, ms);
	});

const getSelectionFromSbapiWithRetry = async (
	selectionId: string,
	shouldContinue: () => boolean,
	isLocaleFallbackEnabled: boolean
): Promise<SelectionDto> => {
	if (!shouldContinue()) {
		if (isLocaleFallbackEnabled) {
			const selectionWithFallback = await getSelectionFromSbapi(selectionId, true);
			if (selectionWithFallback) {
				return selectionWithFallback;
			}
		}

		throw new Error(`Could not get selection ${selectionId} from sbapi`);
	}

	const selection = await getSelectionFromSbapi(selectionId, false);

	if (selection) {
		return selection;
	}

	await sleep(2000);
	return getSelectionFromSbapiWithRetry(selectionId, shouldContinue, isLocaleFallbackEnabled);
};

export const addBetBuilderBetToBetslip =
	(betBuilderBetId: string): ThunkAction =>
	async (dispatch, getState) => {
		const betBuilderId = selectBetBuilderId(getState());
		const isUnifiedFeedEnabled = selectSportsbookUnifiedFeedEnabled(getState());
		const addStartedOn = new Date().getTime();
		dispatch(addToBetslipStarted({ betBuilderId, addStartedOn }));
		try {
			const item = await apiGetBetBuilderItem(betBuilderBetId);
			const betBuilderTimeout = selectSportsbookBetBuilderTimeout(getState()); // Seconds
			const isBetBuilderLocaleFallbackEnabled = selectSportsbookBetBuilderLocaleFallbackEnabled(getState());
			const timeout = addStartedOn + betBuilderTimeout * 1000;

			// It can take some time before the (translation of the) selection is propagated to sbapi
			await sleep(200);

			const selection = await getSelectionFromSbapiWithRetry(
				isUnifiedFeedEnabled ? normalizeUnifiedId(item.selectionId) : item.selectionId,
				() => selectIsAddingToBetslip(getState()) && new Date().getTime() < timeout,
				isBetBuilderLocaleFallbackEnabled
			);

			if (!selectIsAddingToBetslip(getState())) {
				return;
			}
			dispatch(addSelections([selection], SportsbookSelectionTrackingTags.BETBUILDER_BET));

			dispatch(addToBetslipCompleted({ betBuilderId }));
			dispatch(closeBetBuilder());
		} catch (error) {
			if (!selectIsAddingToBetslip(getState())) {
				return;
			}

			dispatch(addToBetslipFailed({ betBuilderId, error }));
			dispatch(closeBetBuilder());
			reportSportsbookError('Failed to add bet builder bet to betslip', error, { betBuilderBetId });
		}
	};

export default betBuilderModalSlice.reducer;
